// Template Variable Replacement Engine

import {
  TemplateVariableContext,
  CaseCptLine,
  Laterality,
  LATERALITY_MODIFIER_MAP,
  ScheduledCase,
  SurgeonTemplate,
} from './types';

/**
 * Replace all {{VARIABLE}} placeholders in a template string
 */
export function renderTemplate(
  template: string,
  context: Partial<TemplateVariableContext>
): string {
  let rendered = template;
  for (const [key, value] of Object.entries(context)) {
    const placeholder = `{{${key}}}`;
    rendered = rendered.split(placeholder).join(value || `[${key}]`);
  }
  return rendered;
}

/**
 * Build a TemplateVariableContext from case data
 */
export function buildVariableContext(params: {
  patient?: ScheduledCase | null;
  template?: SurgeonTemplate | null;
  laterality?: Laterality | null;
  facility?: string;
  cptLines?: CaseCptLine[];
  surgeonName?: string;
}): Partial<TemplateVariableContext> {
  const { patient, template, laterality, facility, cptLines, surgeonName } = params;

  const primaryLines = cptLines?.filter(l => l.section === 'primary') || [];
  const secondaryLines = cptLines?.filter(l => l.section === 'secondary') || [];

  const formatCptLine = (l: CaseCptLine) => {
    const mods = l.modifiers.length > 0 ? `-${l.modifiers.join(',')}` : '';
    return `${l.code}${mods} — ${l.description}`;
  };

  const diagCodes = template?.diagnosis_codes || [];
  // Filter diagnosis codes by laterality if applicable
  const filteredDiag = laterality && diagCodes.length > 1
    ? diagCodes.filter(d => {
        const desc = d.description.toLowerCase();
        if (laterality === 'left') return desc.includes('left') || !desc.includes('right');
        if (laterality === 'right') return desc.includes('right') || !desc.includes('left');
        return true; // bilateral: include all
      })
    : diagCodes;

  return {
    PATIENT_NAME: patient?.patient_name || '[Patient Name]',
    DOB: patient?.patient_dob || '[DOB]',
    MRN: patient?.patient_mrn || '[MRN]',
    CSN: patient?.encounter_id || '[CSN]',
    DATE_OF_SERVICE: patient?.scheduled_date
      ? new Date(patient.scheduled_date).toLocaleDateString()
      : '[Date of Service]',
    SURGEON_NAME: surgeonName || 'Dr. John J. Larkin, MD',
    FACILITY_NAME: facility || patient?.facility_name || '[Facility]',
    PAYER_NAME: patient?.payer_name || '[Payer]',
    PROCEDURE_LATERALITY: laterality
      ? laterality.charAt(0).toUpperCase() + laterality.slice(1)
      : '[Laterality]',
    ANESTHESIA_TYPE: '[General/Regional]',
    DIAGNOSIS_LIST: filteredDiag.map(d => `${d.code} — ${d.description}`).join('\n') || '[Diagnosis codes]',
    PRIMARY_PROCEDURES: primaryLines.map(formatCptLine).join('\n') || '[Primary procedures]',
    SECONDARY_PROCEDURES: secondaryLines.map(formatCptLine).join('\n') || '',
    MODIFIER_MAP: cptLines
      ?.filter(l => l.modifiers.length > 0)
      .map(l => `${l.code}: ${l.modifiers.join(', ')}`)
      .join('\n') || '',
  };
}

/**
 * Apply laterality modifier to CPT lines
 */
export function applyLateralityToLines(
  lines: CaseCptLine[],
  laterality: Laterality | null
): CaseCptLine[] {
  if (!laterality) return lines;

  const latMod = LATERALITY_MODIFIER_MAP[laterality];

  return lines.map(line => {
    // Only apply laterality to primary procedures
    if (line.section !== 'primary') return line;

    // Remove existing laterality modifiers
    const withoutLat = line.modifiers.filter(m => !['LT', 'RT', '50'].includes(m));

    return {
      ...line,
      modifiers: [latMod, ...withoutLat],
    };
  });
}

/**
 * Update narrative text laterality references
 */
export function updateNarrativeLaterality(
  text: string,
  laterality: Laterality
): string {
  const lateralityWord = laterality === 'bilateral' ? 'bilateral' : laterality;

  // Replace common laterality patterns
  let updated = text;
  updated = updated.replace(/\b(left|right|bilateral)\s+(knee|shoulder|hip|wrist|ankle|elbow)/gi,
    `${lateralityWord} $2`);

  return updated;
}
